import React, { useEffect, useState } from "react";
import { supabase, formatError } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Users } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "revisor" });

  const reload = async () => {
    try {
      const sessionRaw = localStorage.getItem("revisapro.session.v2");
      const session = sessionRaw ? JSON.parse(sessionRaw) : null;
      const token = session?.token;
      
      const res = await fetch("/api/team", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Erro ao carregar equipe");
      const json = await res.json();
      setItems(json);
    } catch (err) { toast.error(formatError(err)); }
  };

  useEffect(() => { if (user && user.role !== "revisor") reload(); }, [user]);

  const save = async () => {
    try {
      if (!form.name || !form.email || !form.password) {
        return toast.error("Preencha todos os campos");
      }
      const sessionRaw = localStorage.getItem("revisapro.session.v2");
      const session = sessionRaw ? JSON.parse(sessionRaw) : null;
      const token = session?.token;
      
      const res = await fetch("/api/team", {
        method: "POST",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Erro ao criar membro");
      }
      toast.success("Membro adicionado");
      setOpen(false); resetForm(); reload();
    } catch (err) { toast.error(formatError(err)); }
  };

  const resetForm = () => {
    setForm({ name: "", email: "", password: "", role: "revisor" });
  };

  const rm = async (id) => { 
    if(confirm("Remover este membro da equipe?")) { 
      try { 
        const sessionRaw = localStorage.getItem("revisapro.session.v2");
        const session = sessionRaw ? JSON.parse(sessionRaw) : null;
        const token = session?.token;
        const res = await fetch(`/api/team/${id}`, {
          method: "DELETE",
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Erro ao remover");
        toast.success("Membro removido");
        reload(); 
      } catch (err) { toast.error(formatError(err)); } 
    } 
  };

  const changeRole = async (id, newRole) => {
    try {
      const sessionRaw = localStorage.getItem("revisapro.session.v2");
      const session = sessionRaw ? JSON.parse(sessionRaw) : null;
      const token = session?.token;
      const res = await fetch(`/api/team/${id}`, {
        method: "PATCH",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ role: newRole })
      });
      if (!res.ok) throw new Error("Erro ao alterar nível");
      toast.success("Nível de acesso alterado");
      reload();
    } catch (err) { toast.error(formatError(err)); }
  };

  if (user?.role === "revisor") {
    return <div className="p-8">Acesso restrito.</div>;
  }

  return (
    <div data-testid="team-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">Equipe</div>
          <h1 className="font-serif text-4xl md:text-5xl">Gerenciar Equipe</h1>
          <p className="text-[#4A4A4A] mt-2">Adicione revisores para delegar projetos.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { if(!v) resetForm(); setOpen(v); }}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white" data-testid="new-member-btn">
              <Plus className="w-4 h-4 mr-2" /> Novo membro
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Adicionar à equipe</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-1"><Label className="text-xs font-mono uppercase">Nome</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" /></div>
              <div className="space-y-1"><Label className="text-xs font-mono uppercase">E-mail (Login)</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" /></div>
              <div className="space-y-1"><Label className="text-xs font-mono uppercase">Senha Provisória</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none" /></div>
              
              <div className="space-y-1 mt-2">
                <Label className="text-xs font-mono uppercase">Nível de Acesso</Label>
                <div className="flex gap-2">
                  <Button type="button" variant={form.role === "revisor" ? "default" : "outline"} onClick={() => setForm({ ...form, role: "revisor" })} className="rounded-none flex-1">
                    Revisor
                  </Button>
                  <Button type="button" variant={form.role === "user" ? "default" : "outline"} onClick={() => setForm({ ...form, role: "user" })} className="rounded-none flex-1" title="Gerentes têm acesso ao painel inteiro, projetos e financeiro.">
                    Gerente
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Revisores só veem projetos atribuídos a eles. Gerentes veem tudo.</p>
              </div>

              <Button onClick={save} className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white w-full py-6 text-lg mt-4">
                Adicionar Membro
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 text-center border border-black/10 rounded-none bg-white">
          <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <p className="text-[#8A8A8A] font-mono">Nenhum membro na sua equipe.</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {items.map((p) => (
            <Card key={p.id} className="bg-white border border-black/10 rounded-none p-5 flex items-center justify-between">
              <div>
                <h3 className="font-serif text-xl">{p.name}</h3>
                <div className="text-sm text-[#4A4A4A] font-mono mt-1">{p.email}</div>
              </div>
              <div className="flex items-center gap-4">
                <Select value={p.role === "user" ? "user" : "revisor"} onValueChange={(v) => changeRole(p.id, v)}>
                  <SelectTrigger className="w-[120px] rounded-none h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revisor">Revisor</SelectItem>
                    <SelectItem value="user">Gerente</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" className="rounded-none h-9 w-9" onClick={() => rm(p.id)}>
                  <Trash2 className="w-4 h-4 text-[#D9381E]" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
